import { motion, useMotionTemplate, useMotionValue } from 'framer-motion'
import { useState, type MouseEvent, type FormEvent } from 'react'
import { SITE } from '../../lib/links'
import { useT } from '../../i18n/LocaleContext'

export default function BlogNewsletter() {
  const { t } = useT()
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const mouseX = useMotionValue(-500)
  const mouseY = useMotionValue(-500)

  const onMove = (e: MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    mouseX.set(e.clientX - r.left)
    mouseY.set(e.clientY - r.top)
  }
  const onLeave = () => {
    mouseX.set(-500)
    mouseY.set(-500)
  }

  const spotlight = useMotionTemplate`radial-gradient(520px circle at ${mouseX}px ${mouseY}px, rgba(94,132,255,0.18), transparent 65%)`

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!email) return
    // Route the subscription request to contact@yaslan.be
    const subject = encodeURIComponent('Inscription newsletter Yaslan')
    const body = encodeURIComponent(
      `Bonjour,\n\nJe souhaite m'inscrire à la newsletter Yaslan.\n\nAdresse e-mail : ${email}\n\nMerci !`,
    )
    window.location.href = `mailto:${SITE.email}?subject=${subject}&body=${body}`
    setSubmitted(true)
    setTimeout(() => {
      setEmail('')
      setSubmitted(false)
    }, 3200)
  }

  return (
    <section className="relative max-w-6xl mx-auto px-6 pb-28">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        className="relative overflow-hidden rounded-3xl bg-ink-950 text-white p-10 sm:p-16"
      >
        {/* Cursor spotlight */}
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: spotlight }}
        />

        {/* Static radial blue glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 -right-24 h-[420px] w-[420px] rounded-full opacity-70 blur-3xl"
          style={{
            background:
              'radial-gradient(circle, rgba(59,93,255,0.55), rgba(59,93,255,0) 70%)',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-24 h-[360px] w-[360px] rounded-full opacity-60 blur-3xl"
          style={{
            background:
              'radial-gradient(circle, rgba(94,132,255,0.4), rgba(59,93,255,0) 70%)',
          }}
        />

        {/* Subtle grid overlay */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            maskImage:
              'radial-gradient(ellipse at center, black 30%, transparent 75%)',
          }}
        />

        <div className="relative grid md:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-accent-200"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-400" />
              </span>
              <span className="font-medium">{t('blog.newsletterBadge')}</span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="mt-5 font-semibold tracking-[-0.02em] text-3xl sm:text-4xl leading-[1.1]"
            >
              {t('blog.newsletterTitleLine1')}
              <br />
              <span className="font-semibold bg-gradient-to-r from-accent-300 via-accent-200 to-white bg-clip-text text-transparent">
                {t('blog.newsletterTitleHighlight')}
              </span>
              .
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-5 text-white/70 leading-relaxed max-w-md"
            >
              {t('blog.newsletterCopy')}
            </motion.p>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            onSubmit={onSubmit}
            className="relative"
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <label className="relative flex-1 group">
                <span className="sr-only">{t('blog.newsletterEmailLabel')}</span>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('blog.newsletterPlaceholder')}
                  className="w-full rounded-full bg-white/5 border border-white/15 text-white placeholder-white/40 px-5 py-3.5 pr-12 outline-none focus:border-accent-400 focus:bg-white/10 transition-all"
                />
                <motion.span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 rounded-full opacity-0 group-focus-within:opacity-100 transition-opacity"
                  style={{
                    boxShadow: '0 0 0 4px rgba(94,132,255,0.18)',
                  }}
                />
              </label>

              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="relative overflow-hidden rounded-full bg-accent-700 hover:bg-accent-800 text-white font-semibold px-6 py-3.5 shadow-[0_18px_50px_-12px_rgba(59,93,255,0.7)] transition-colors"
              >
                <span className="relative z-10 inline-flex items-center gap-2">
                  {submitted ? (
                    <>
                      <motion.svg
                        initial={{ scale: 0, rotate: -90 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 16 }}
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.6"
                      >
                        <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                      </motion.svg>
                      {t('blog.newsletterSubmitted')}
                    </>
                  ) : (
                    <>
                      {t('blog.newsletterSubmit')}
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.4"
                      >
                        <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </>
                  )}
                </span>
                {/* Shimmer sweep */}
                <motion.span
                  aria-hidden
                  initial={{ x: '-120%' }}
                  animate={{ x: '120%' }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: 'linear', repeatDelay: 1.2 }}
                  className="absolute inset-y-0 w-1/3"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, rgba(255,255,255,0.28), transparent)',
                  }}
                />
              </motion.button>
            </div>

            <p className="mt-4 text-xs text-white/50">
              {t('blog.newsletterPrivacy')}
            </p>

            <div className="mt-6 flex items-center gap-4 text-xs text-white/60">
              <div className="flex -space-x-2">
                {['#3b5dff', '#5e84ff', '#8aa7ff', '#b8c9ff'].map((c) => (
                  <span
                    key={c}
                    className="w-7 h-7 rounded-full border-2 border-ink-950"
                    style={{ background: c }}
                  />
                ))}
              </div>
              <span>
                {t('blog.newsletterSocialProofPart1')}{' '}
                <span className="text-white font-semibold">{t('blog.newsletterSocialProofCount')}</span>{' '}
                {t('blog.newsletterSocialProofPart2')}
              </span>
            </div>
          </motion.form>
        </div>
      </motion.div>
    </section>
  )
}
